import { useCallback, type ReactNode } from "react";
import { DolfinProvider } from "@/components/DolfinProvider";
import type { components } from "@dolfin/api-admin";
import { isTokenDue } from "@dolfin/lib";
import { useAuth } from "@/auth/AuthContext";
import { sessionRefresher } from "@/auth/session";

type AuthResponse = components["schemas"]["AuthResponse"];

/**
 * Hands DolfinProvider the stored session token, renewing via the refresh cookie when stale. Real integrators
 * implement `fetchToken` against their own backend (POST /auth/codes + /auth/exchange behind their API key).
 */
export function DolfinAppProvider({ children }: { children: ReactNode }) {
  const { user, token, activeOrganisationId, isLoading } = useAuth();

  // userId is ignored: the sandbox already has a session. Real integrators exchange it for a token.
  const fetchToken = useCallback(async (_args: { userId: string }): Promise<AuthResponse> => {
    if (!user || !token) {
      throw new Error("DolfinAppProvider: cannot fetch token - user not signed in.");
    }
    // Read the stored token, not the render's: another tab or the API client may have refreshed since.
    let current = sessionRefresher.readStoredToken() ?? token;
    if (isTokenDue(current)) {
      const outcome = await sessionRefresher.refresh(current);
      if (outcome.kind !== "refreshed") {
        throw new Error("DolfinAppProvider: session has ended - sign in again.");
      }
      current = outcome.token;
    }
    return {
      token: current,
      userId: user.userId,
      email: user.email,
      organisations: user.organisations as AuthResponse["organisations"],
      clients: user.clients as AuthResponse["clients"],
    };
  }, [user, token]);

  // AuthContext hydrates in an effect; mounting before then would fire requests without auth.
  if (isLoading) return null;

  return (
    <DolfinProvider
      fetchToken={fetchToken}
      userId={user?.userId}
      organisationId={activeOrganisationId ?? undefined}
      baseUrl="/api/proxy"
    >
      {children}
    </DolfinProvider>
  );
}
